import { useState, useMemo, useCallback } from 'react';

/**
 * useLoadMore — simple in-memory pagination for list rendering.
 *
 * Use this when you have a known-total array of items and want to render
 * them in pages of N (default 10) with a "Load more" button to grow the
 * visible window.
 *
 * Returns:
 *   - visible: the items to render (slice of `items`)
 *   - hasMore: boolean — are there more items beyond the visible window?
 *   - loadMore(): show the next `pageSize` items
 *   - reset(): collapse back to the initial page
 *   - total: total item count
 *   - visibleCount: how many items are currently visible
 *
 * Phase-16: replaces "render every row ever" with progressive loading
 * so the page is fast and lightweight.
 */
export default function useLoadMore(items, pageSize = 10, initial = null) {
  const [count, setCount] = useState(initial ?? pageSize);

  const visible = useMemo(() => {
    if (!items) return [];
    return items.slice(0, count);
  }, [items, count]);

  const hasMore = useMemo(() => {
    if (!items) return false;
    return count < items.length;
  }, [items, count]);

  const loadMore = useCallback(() => {
    setCount((c) => Math.min(c + pageSize, items?.length ?? 0));
  }, [items, pageSize]);

  const reset = useCallback(() => {
    setCount(pageSize);
  }, [pageSize]);

  return {
    visible,
    hasMore,
    loadMore,
    reset,
    total: items?.length ?? 0,
    visibleCount: visible.length,
  };
}
